import { ArrowDownRight, ArrowUpRight, Check, StickyNote } from "lucide-react";
import { useState } from "react";
import useTranslation from "../../client/hooks/useTranslation";
import { useLiteData } from "../data/LiteDataProvider";
import { byDay, net } from "../lib/stats";
import type { LiteTrade } from "../lib/types";
import { Empty, Pnl, cardCls } from "./ui";

export function SideTag({ side }: { side: LiteTrade["side"] }) {
  const t = useTranslation().lite.common;
  const long = side === "long";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium px-1.5 py-0.5 rounded-md ${long ? "bg-pnlPositive/10 text-pnlPositive" : "bg-pnlNegative/10 text-pnlNegative"}`}>
      {long ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {long ? t.long : t.short}
    </span>
  );
}

export function TradeRow({ trade, onSelect, showAccount = true, selected, checked }: {
  trade: LiteTrade;
  onSelect: (t: LiteTrade) => void;
  showAccount?: boolean;
  selected?: boolean;
  /** Mode sélection : case à cocher en tête de ligne (undefined = mode normal). */
  checked?: boolean;
}) {
  const t = useTranslation().lite;
  const { accountsById, strategies, fmt } = useLiteData();
  const account = trade.accountId ? accountsById.get(trade.accountId) : undefined;
  const strategy = strategies.find(s => trade.strategyIds.includes(s.id));
  return (
    <button
      type="button"
      onClick={() => onSelect(trade)}
      role={checked !== undefined ? "checkbox" : undefined}
      aria-checked={checked}
      className={`w-full grid ${checked !== undefined ? "grid-cols-[20px_52px_1fr_auto] md:grid-cols-[20px_60px_minmax(120px,1fr)_minmax(140px,1fr)_minmax(160px,1.2fr)_56px_120px]" : "grid-cols-[52px_1fr_auto] md:grid-cols-[60px_minmax(120px,1fr)_minmax(140px,1fr)_minmax(160px,1.2fr)_56px_120px]"} items-center gap-3 px-4 md:px-5 py-3 text-left text-sm transition-colors ${selected || checked ? "bg-muted" : "hover:bg-muted/60"}`}
    >
      {checked !== undefined && <CheckBox checked={checked} />}
      <span className="font-mono text-xs text-muted-foreground">{fmt.time(trade.openedAt)}</span>
      <span className="flex items-center gap-2 min-w-0">
        <span className="font-semibold">{trade.symbol}</span>
        <SideTag side={trade.side} />
        {trade.note && <StickyNote size={13} className="text-muted-foreground/70 shrink-0" aria-label={t.trade.note} />}
        {showAccount && <span className="md:hidden text-xs text-muted-foreground/70 truncate">{account?.name ?? t.trade.noAccount}</span>}
      </span>
      <span className="hidden md:block text-muted-foreground truncate">{showAccount ? account?.name ?? t.trade.noAccount : ""}</span>
      <span className="hidden md:flex items-center gap-2 text-muted-foreground min-w-0">
        {strategy && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: strategy.color }} />}
        <span className="truncate">{strategy?.name ?? ""}</span>
      </span>
      <span className="hidden md:block font-mono text-muted-foreground text-right">×{trade.qty}</span>
      {trade.closed ? <Pnl value={net(trade)} decimals className="text-right font-medium" /> : <span className="text-right text-xs text-muted-foreground">{t.trade.open}</span>}
    </button>
  );
}

/** Trades groupés par jour : une journée = une session, avec son total. */
export function TradeList({ trades, onSelect, selectedId, showAccount = true, pageSize = 7, selection, onToggle }: {
  trades: LiteTrade[];
  onSelect: (t: LiteTrade) => void;
  selectedId?: string;
  showAccount?: boolean;
  pageSize?: number;
  /** Mode sélection (suppression groupée) : ids cochés ; un clic coche au lieu d'ouvrir le trade. */
  selection?: Set<string>;
  /** Coche / décoche des trades (une ligne, ou toute une journée). */
  onToggle?: (ids: string[], checked: boolean) => void;
}) {
  const t = useTranslation().lite;
  const { fmt } = useLiteData();
  const [shown, setShown] = useState(pageSize);
  const days = byDay(trades);
  if (!days.length) return <div className={cardCls}><Empty title={t.common.noTrade}>{t.common.noTradeHint}</Empty></div>;

  return (
    <div className="space-y-4">
      {days.slice(0, shown).map(day => {
        const sorted = [...day.trades].sort((a, b) => a.openedAt.localeCompare(b.openedAt));
        const closed = sorted.filter(x => x.closed);
        const wins = closed.filter(x => net(x) > 0).length;
        return (
          <section key={day.key} className={`${cardCls} overflow-hidden`}>
            <header className="flex items-center justify-between gap-4 px-4 md:px-5 py-3.5 border-b border-border">
              {selection && (() => {
                const ids = sorted.map(x => x.id);
                const all = ids.every(id => selection.has(id));
                return (
                  <button type="button" role="checkbox" aria-checked={all} aria-label={fmt.dateLong(day.date)} onClick={() => onToggle?.(ids, !all)} className="shrink-0">
                    <CheckBox checked={all} />
                  </button>
                );
              })()}
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold">{fmt.dateLong(day.date)}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t.trade.dayMeta(sorted.length, fmt.pct(closed.length ? wins / closed.length : 0), fmt.time(sorted[0].openedAt), fmt.time(sorted.at(-1)!.closedAt))}
                </p>
              </div>
              <Pnl value={day.pnl} className="text-lg font-semibold" />
            </header>
            <div className="divide-y divide-border">
              {sorted.map(tr => (
                <TradeRow
                  key={tr.id}
                  trade={tr}
                  onSelect={selection ? x => onToggle?.([x.id], !selection.has(x.id)) : onSelect}
                  showAccount={showAccount}
                  selected={tr.id === selectedId}
                  checked={selection ? selection.has(tr.id) : undefined}
                />
              ))}
            </div>
          </section>
        );
      })}
      {shown < days.length && (
        <div className="flex justify-center">
          <button type="button" className="h-9 px-4 rounded-xl border border-border text-sm hover:bg-muted" onClick={() => setShown(s => s + pageSize)}>
            {t.trade.showPrevious(days.length - shown)}
          </button>
        </div>
      )}
    </div>
  );
}

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span aria-hidden className={`grid h-4 w-4 place-items-center rounded border transition-colors ${checked ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/50"}`}>
      {checked && <Check size={12} strokeWidth={3} />}
    </span>
  );
}
